import { DrawerContext, DrawerFitted } from 'app/components/Common/Drawer/Drawer';
import React from 'react';
import { AvailableStartDate } from './AvailableStartDate';
import { BestTimeToCall } from './BestTimeToCall';
import EditIcon from 'app/assets/images/IconImages/GroupEditIcon.svg';
import { Box, Divider, IconButton, Table, TableBody, TableCell, TableRow } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { LayoutGrid, LayoutGridItem } from 'app/layout/LayoutGrid';
import { RequestedShifts } from './RequestedShifts';
import { TravelsWithPartnerStatus } from './TravelsWithPartnerStatus';
import { SellingPoints } from './SellingPoints';
import { useFormContext } from 'react-hook-form';
import { TravelPartner } from './TravelPartner';
import styledComponent from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { selectPlacementHeaderData } from '../../../../../../store/redux-store/placement-details/selectors';
import { GetMomentOfDate } from 'utils/dates/moment';
import { missingField } from 'app/constants';
import { editFromDrawerAccessor, editableFields } from '../../editUtils';
import { selectGlanceDrawerState } from '../store/placementPageAttributes.selector';
import { pageAttributesActions } from '../store/placementPageAttributes.redux';
import { SummaryRequestedTimeOff } from '../../PlacementTabPanel/PlacementSummaryTab/RequirementsAndQualifications/SummaryRequestedTimeOff';
import ComplianceStatusProgressDrawer from './CSPNotes/ComplianceStatusProgressDrawer';
import { PlacementEndDate } from './PlacementEndDate';
import { PlacementStartDate } from './PlacementStartDate';
import { useDecision } from '@optimizely/react-sdk';
import { ff_new_create_placement_modal_layout } from 'app/constants/FeatureFlags/Placement/Keys';
import { SummaryTimeOffRequest } from '@AMIEWEB/Placement/CreateNewPlacement/Candidate/Edit/SummaryTimeOffRequest';

const useStyles = makeStyles()({
  root: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: '6px',
    cursor: 'pointer',
  },
  edit: {
    color: '#006FB9',
    fontSize: 14,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  title: {
    color: '#333333',
    fontSize: 16,
    fontWeight: 450,
    padding: 12,
  },
  titleGrid: {
    height: 52,
    borderBottom: '1px solid #c0c0c0',
  },
  closeIcon: {
    padding: 0,
  },
  divider: {
    margin: '8px 12px',
  },
  chippedCell: {
    paddingBottom: '0px !important',
  },
  sellingPoints: {
    whiteSpace: 'pre-wrap',
  },
  requestedTimeOff: {
    paddingLeft: '30px',
    paddingTop: '10px',
  },
  drawerContent: {
    height: 'calc(100vh - 100px)',
    overflow: 'auto',
  },
  drawerContentOverflow: {
    overflow: 'hidden',
  },
  topAlign: {
    verticalAlign: 'top',
  },
  complianceStatusWrapper: {
    marginRight: '12px',
  },
});

export const GlanceDrawer = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const open = useSelector(selectGlanceDrawerState);

  const changeDrawerState = state => dispatch(pageAttributesActions.setGlanceDrawerState(state));

  return (
    <>
      <Box className={classes.complianceStatusWrapper}>
        <ComplianceStatusProgressDrawer />
      </Box>
      <Box className={classes.root} onClick={() => changeDrawerState(true)}>
        <img src={EditIcon} alt="Edit" />
        <div className={classes.edit}>{t('detailsPage.edit')}</div>
      </Box>
      <DrawerFitted
        height="100vh"
        overflow="unset"
        open={open}
        onDrawerClose={() => changeDrawerState(false)}
        width={400}
        top={0}
      >
        <LayoutGrid container direction="column">
          <LayoutGridItem container direction="row" classes={{ root: classes.titleGrid }} wrap="nowrap">
            <LayoutGridItem container alignContent="center" xs={10} classes={{ root: classes.title }}>
              {t('order.orderDetails.orderDetailsView.atAGlance.sectionTitle3')}
            </LayoutGridItem>
            <LayoutGridItem container justifyContent="center" xs={2}>
              <IconButton
                color="primary"
                disableFocusRipple
                disableRipple
                disableTouchRipple
                classes={{ root: classes.closeIcon }}
                onClick={() => changeDrawerState(false)}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </LayoutGridItem>
          </LayoutGridItem>
          <GlanceDrawerContent />
        </LayoutGrid>
      </DrawerFitted>
    </>
  );
};

const GlanceDrawerContent = () => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const { watch } = useFormContext();
  const { lastUpdatedBy, lastUpdatedAt } = useSelector(selectPlacementHeaderData);
  const travelsWithPartnerStatus = watch('travelsWithPartner');
  const lastUpdatedMomnetDate = GetMomentOfDate(lastUpdatedAt);
  const { popperOpen } = React.useContext(DrawerContext);
  const [newPlacementLayoutFlag] = useDecision(ff_new_create_placement_modal_layout);

  return (
    <LayoutGridItem className={cx(classes.drawerContent, { [classes.drawerContentOverflow]: popperOpen })}>
      <TableSects
        attributes={[
          { title: t('placement.profile.glanceDrawer.lastUpdatedAt') },
          { title: t('placement.profile.glanceDrawer.lastUpdatedBy') },
        ]}
      >
        {lastUpdatedMomnetDate.isValid() ? lastUpdatedMomnetDate.format('MM/DD/YYYY h:mm A') : missingField}
        {lastUpdatedBy || missingField}
      </TableSects>
      <Divider classes={{ root: classes.divider }} />
      <TableSects
        attributes={[
          { title: t('placement.profile.header.placementStartDate') },
          { title: t('placement.profile.header.placementEndDate') },
          { title: t('placement.profile.header.availableStartDate') },
          { title: t('placement.create.popup.requestedShifts') },
          { title: t('placement.profile.header.bestTimeTocall') },
        ]}
      >
        <PlacementStartDate id={`${editFromDrawerAccessor}-${editableFields.placementStartDate}`} maxWidth={215} />
        <PlacementEndDate id={`${editFromDrawerAccessor}-${editableFields.placementEndDate}`} maxWidth={215} />
        <AvailableStartDate id={`${editFromDrawerAccessor}-${editableFields.availabilityDate}`} />
        <RequestedShifts id={`${editFromDrawerAccessor}-${editableFields.requestedShift}`} />
        <BestTimeToCall id={`${editFromDrawerAccessor}-${editableFields.bestTimeToCall}`} />
      </TableSects>
      <Divider classes={{ root: classes.divider }} />
      <TableSects
        attributes={[
          { title: t('placement.profile.tabs.candidateSellingPoints.travelsWithPartner') },
          ...(travelsWithPartnerStatus
            ? [{ title: t('placement.profile.tabs.candidateSellingPoints.travelsWithPartnerName') }]
            : []),
        ]}
      >
        <TravelsWithPartnerStatus id={`${editFromDrawerAccessor}-${editableFields.travelsWithPartner}`} />
        {travelsWithPartnerStatus && <TravelPartner id={`${editFromDrawerAccessor}-${editableFields.travelPartner}`} />}
      </TableSects>
      <Divider classes={{ root: classes.divider }} />

      {!newPlacementLayoutFlag.enabled ? (
        <React.Fragment>
          <TableRow>
            <CustomCellKey variant="head" classes={{ root: classes.chippedCell }}>
              {t('placement.create.popup.timeOffRequests')}
            </CustomCellKey>
          </TableRow>
          <TableRow>
            <div className={classes.requestedTimeOff}>
              <SummaryRequestedTimeOff
                disableLabel
                id={`${editFromDrawerAccessor}-${editableFields.requestedTimeOff}`}
              />
            </div>
          </TableRow>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <div style={{ width: '90%', paddingLeft:'14px' }}>
            <SummaryTimeOffRequest />
          </div>
        </React.Fragment>
      )}

      <Divider classes={{ root: classes.divider }} />
      <TableSects
        attributes={[
          {
            title: t('placement.profile.tabs.candidateSellingPoints.placementSellingPoints'),
            isColumnCell: true,
          },
        ]}
        classes={classes}
      >
        <div className={classes.sellingPoints}>
          <SellingPoints id={`${editFromDrawerAccessor}-${editableFields.sellingPoints}`} />
        </div>
      </TableSects>
    </LayoutGridItem>
  );
};

const TableSects = ({ attributes, ...rest }) => {
  const { classes, cx } = useStyles();
  return (
    <Table>
      <TableBody>
        {attributes.map(({ title, isColumnCell = false, topAlign = false }, index) =>
          isColumnCell ? (
            <React.Fragment key={index.toString()}>
              <TableRow className={cx({ [classes.topAlign]: topAlign })}>
                <CustomCellKey variant="head" classes={{ root: rest.classes.chippedCell }}>
                  {title.endsWith(':') ? title : `${title}:`}
                </CustomCellKey>
              </TableRow>
              <TableRow>
                <CustomCellValue>{rest.children}</CustomCellValue>
              </TableRow>
            </React.Fragment>
          ) : (
            <TableRow key={index.toString()} className={cx({ [classes.topAlign]: topAlign })}>
              <CustomCellKey variant="head">{title.endsWith(':') ? title : `${title}:`}</CustomCellKey>
              <CustomCellValue>{rest.children[index]}</CustomCellValue>
            </TableRow>
          ),
        )}
      </TableBody>
    </Table>
  );
};

const cellStyle = `
border-bottom: none;
font-weight: 500;
font-size: 14px;
color: #555;
padding: 12px;
`;

const CustomCellKey = styledComponent(TableCell)`
  ${cellStyle}
  width: 42%;
`;

const CustomCellValue = styledComponent(TableCell)`
  ${cellStyle}
  width: 58%;
`;
